<script setup lang="ts">
/**
 * Ambient QR-twinkle background, used as a brand-textured fill behind
 * the dead-center "card on empty page" layouts — login, access denied,
 * not found. Picks up the splash motif and lets it bleed into the rest
 * of the auth journey instead of cutting hard from a lively splash to
 * a stark form.
 *
 * The boot splash (src/app/splash.ts) is the focal version (25×25 grid
 * with finders + "EZQR" wordmark + busy twinkling). This is the
 * *ambient* version:
 *
 *  - No finders, no wordmark — purely sparse dots so the eye reads
 *    "texture" not "logo I should look at."
 *  - Cell count is computed from the actual element size on mount and
 *    on resize, then capped (see `MAX_DOTS`) so a large display doesn't
 *    allocate thousands of nodes. The grid auto-fills and clips.
 *  - The shimmer is ONE animated highlight band sweeping across static
 *    dots, not a per-dot opacity loop — paint cost stays flat no matter
 *    how many dots are on screen.
 *  - Low contrast by default — the texture lives behind a card, the
 *    card stays the focal layer.
 *  - `prefers-reduced-motion` drops the sweep to a static, slightly
 *    dimmer texture. The dots stay; the motion stops.
 *
 * Theme-reactive via `currentColor` — caller controls the tint by
 * setting `text-*` on a wrapping element.
 */
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import { useDebounceFn } from '@vueuse/core'

interface Props {
  /**
   * Dot pattern density. `loose` for ambient backgrounds (default — fewer
   * dots, more breathing room); `dense` to approach the splash feel.
   */
  density?: 'loose' | 'dense'
  /**
   * Tailwind opacity utility for the whole layer (e.g. `opacity-[0.06]`).
   * Keep it low — the layer sits behind real content.
   */
  opacityClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  density: 'loose',
  opacityClass: 'opacity-[0.06]',
})

const root = useTemplateRef<HTMLElement>('root')
const dotCount = ref(0)

// Cell sizes are the resolved values of the `clamp()` lookup below.
// Recomputing here would mean parsing CSS — simpler to mirror the same
// numeric upper bound the CSS uses and let the auto-fill grid do the
// rest.
const CELL_CEIL_PX = computed(() => (props.density === 'dense' ? 14 : 22))

/**
 * Hard ceiling on node count. On a large display the viewport-derived
 * count runs into the thousands; past a few hundred dots the texture
 * reads identically but the DOM/paint cost climbs linearly. The grid
 * auto-fills and overflow is clipped, so capping just stops drawing the
 * dots that fall outside the first ~MAX_DOTS cells — visually the field
 * still fills because the cap is chosen to cover a large viewport at the
 * loose density. Keep generous enough that a 1440p screen still looks
 * full, small enough that paint stays cheap.
 */
const MAX_DOTS = 600

function recomputeDotCount(): void {
  const el = root.value
  if (!el) return
  const w = el.clientWidth
  const h = el.clientHeight
  if (w === 0 || h === 0) return
  // Use the clamp ceiling as the conservative cell size. If the clamp
  // resolves to a smaller cell on this viewport, we generate a few
  // extra dots and the grid auto-wraps — cheap. Add ~20% margin so
  // resize jitter doesn't briefly underfill the edges.
  const cell = CELL_CEIL_PX.value
  const cols = Math.ceil(w / cell)
  const rows = Math.ceil(h / cell)
  dotCount.value = Math.min(MAX_DOTS, Math.ceil(cols * rows * 1.2))
}

const debouncedRecompute = useDebounceFn(recomputeDotCount, 80)

let ro: ResizeObserver | null = null

onMounted(() => {
  recomputeDotCount()
  if (typeof ResizeObserver !== 'undefined' && root.value) {
    ro = new ResizeObserver(() => debouncedRecompute())
    ro.observe(root.value)
  }
})

onBeforeUnmount(() => {
  ro?.disconnect()
  ro = null
})

// The grid template uses CSS clamp() so the cell scales smoothly with
// the viewport — same strategy as the boot splash. The ceil values
// here MUST match CELL_CEIL_PX above so dotCount stays correct.
const cellSize = computed(() =>
  props.density === 'dense' ? 'clamp(8px, 1.8vmin, 14px)' : 'clamp(14px, 3vmin, 22px)',
)
</script>

<template>
  <div
    ref="root"
    aria-hidden="true"
    :class="[
      'pointer-events-none absolute inset-0 overflow-hidden text-foreground',
      'splash-texture',
      opacityClass,
    ]"
    :style="{ '--st-cell': cellSize }"
  >
    <div class="splash-texture-grid">
      <i v-for="i in dotCount" :key="i" class="splash-texture-dot" />
    </div>
    <!-- The shimmer. A single diagonal highlight band sweeps across the
         static dot field via one transform animation, instead of every
         dot running its own infinite opacity keyframe. One animated
         layer composites far cheaper than thousands of nodes, so the
         CPU/paint cost is flat regardless of how many dots are on
         screen. The band is masked to the dots through `mix-blend-mode`
         so only the dots beneath it brighten. -->
    <div class="splash-texture-shimmer" />
  </div>
</template>

<style scoped>
.splash-texture {
  /* Promote to its own layer so the shimmer animates on the compositor
     and never repaints the dot field underneath. */
  isolation: isolate;
}

.splash-texture-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, var(--st-cell));
  grid-auto-rows: var(--st-cell);
  width: 100%;
  height: 100%;
}

.splash-texture-dot {
  width: 35%;
  height: 35%;
  justify-self: center;
  align-self: center;
  border-radius: 1.5px;
  background: currentColor;
  /* Static base brightness — no per-dot animation. The shimmer band
     supplies the motion. */
  opacity: 0.18;
}

/* A wide, soft diagonal highlight twice the layer's height, slid from
   above the top edge to below the bottom on a slow loop. `screen` blend
   only ever brightens, so the band lifts the dots it overlaps and
   leaves the rest at their base opacity. */
.splash-texture-shimmer {
  position: absolute;
  inset: -50% 0;
  pointer-events: none;
  mix-blend-mode: screen;
  background: linear-gradient(
    180deg,
    transparent 0%,
    color-mix(in srgb, currentColor 22%, transparent) 45%,
    color-mix(in srgb, currentColor 38%, transparent) 50%,
    color-mix(in srgb, currentColor 22%, transparent) 55%,
    transparent 100%
  );
  will-change: transform;
  animation: st-sweep 7s linear infinite;
}

@keyframes st-sweep {
  0% {
    transform: translateY(-40%);
  }
  100% {
    transform: translateY(40%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .splash-texture-dot {
    opacity: 0.2;
  }
  .splash-texture-shimmer {
    animation: none;
    /* Park the band off-field so the texture sits perfectly static. */
    display: none;
  }
}
</style>
